/**
 * GraphSerializer — Bidirectional Graph ↔ JSON serialization for Redprint.
 *
 * The serialized format (version, compact edge keys, node x/y + inputs/comment/
 * config, variable exposed/description) is **identical** to the generic
 * serializer in @engine/graph — Redprint's graph model is a structural
 * superset of `BaseGraphData`. This module is therefore a thin typed wrapper
 * that delegates to the base serializer, fixing the type parameters to
 * Redprint's concrete `NodeInstance` / `GraphVariable` so callers keep getting
 * `GraphData` back. No domain-specific extras are needed (Redprint stores no
 * extra per-node/per-variable fields on disk).
 */

import {
  serializeGraph as baseSerializeGraph,
  deserializeGraph as baseDeserializeGraph,
  graphToJSON as baseGraphToJSON,
  graphFromJSON as baseGraphFromJSON,
} from '@engine/graph';
import type { GraphData, NodeInstance, GraphVariable } from '../types';

// Re-export the serialized-format types from the canonical source.
export type {
  SerializedGraph,
  SerializedNode,
  SerializedEdge,
  SerializedVariable,
} from '@engine/graph';

/**
 * Serialize a GraphData to a compact JSON-serializable object.
 */
export function serializeGraph(graph: GraphData) {
  return baseSerializeGraph<NodeInstance, GraphVariable>(graph);
}

/**
 * Deserialize a JSON object back into a GraphData.
 * Throws on structural errors or a future format version.
 */
export function deserializeGraph(json: unknown): GraphData {
  return baseDeserializeGraph<NodeInstance, GraphVariable>(json) as GraphData;
}

/**
 * Serialize a graph to a JSON string.
 */
export function graphToJSON(graph: GraphData, pretty = false): string {
  return baseGraphToJSON<NodeInstance, GraphVariable>(graph, { pretty });
}

/**
 * Parse a JSON string into GraphData.
 */
export function graphFromJSON(json: string): GraphData {
  return baseGraphFromJSON<NodeInstance, GraphVariable>(json) as GraphData;
}
